import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Header } from '../../common/Header/Header.jsx';
import { SelectPerPage } from '../../common/Forms/Input/Input.jsx';
import { PaymentsChangesTable } from '../../common/Tables/Tables.jsx';
import { paymentChangesTableHeader } from '../../../assets/helpers/tables.js';
import { notify } from '../../../assets/helpers/toast.js';
import { salesRoles } from '../../../assets/helpers/roles.js';
import { FilterDatePicker } from '../../common/Forms/Input/Input.jsx';
import moment from 'moment';
import { filterParams } from '../../../assets/helpers/funciones.js';
import './Changes.scss';
import authService from '../../../services/authService.js';
import { paymentsService } from '../../../services/paymentsService.js';
import { EditChangeModal } from '../../common/Modals/Modals.jsx';
import { LoadSpinner } from '../../common/LoadSpinner/LoadSpinner.jsx';
import * as schemas from '../../../assets/schemas/schemas.js';
import { yupResolver } from '@hookform/resolvers/yup';
import { productService } from '../../../services/productService.js';
import { rateService } from '../../../services/rateService.js';
import { paymentTypeOptions as paymentTypesOptions } from '../../../assets/helpers/options';
import { useDropzone } from 'react-dropzone';

const itemsPerpage = [10, 25, 50, 100, 500];

export const Changes = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
  } = useForm({
    resolver: yupResolver(schemas.editCashSchema()),
  });

  const tableRef = useRef(null);
  const [dollarRate, setDollarRate] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openEditPaymentModal, setOpenEditPaymentModal] = useState({
    isOpen: false,
    payment: '',
  });
  const [dateFrom, setDateFrom] = useState(
    new Date(moment().subtract(1, 'months'))
  );
  const [dateTo, setDateTo] = useState(new Date(moment()));
  const [validatePaymentTable, setValidatePaymentTable] = useState({
    limit: itemsPerpage[2],
    offset: 0,
    data: [],
    count: 0,
    page: 1,
    dateFrom: '',
    dateTo: '',
    isLoading: false,
    searchParam: '',
  });

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();

      reader.onload = () => {
        // Do whatever you want with the file contents
      };
      reader.readAsArrayBuffer(file);

      if (
        !['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'].includes(
          file.type
        )
      ) {
        notify('Agregar un formato valido de imagen', 'error');
        return null;
      }
      let maxWeight = 5;
      if (file.size * 0.000001 > maxWeight) {
        notify(`La imagen no debe superar los ${maxWeight} MB`, 'error');
      } else {
        setFileUploaded(file);
      }
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });
  const [fileUploaded, setFileUploaded] = useState('');

  const appendFile = (data) => {
    const formData = new FormData();
    formData.append('paid', data?.paid);
    formData.append('amount', data?.amount);
    formData.append('amountBs', data?.amountBs);
    formData.append('method', data?.method);
    formData.append('ref', data?.ref);
    formData.append('date', data?.date);
    if (data?.bank) {
      formData.append('bank', data?.bank);
    }
    if (data?.phone) {
      formData.append('phone', data?.phone);
    }
    if (data?.identificationType) {
      formData.append('identificationType', data?.identificationType);
    }
    if (data?.identification) {
      formData.append('identification', data?.identification);
    }

    if (fileUploaded) {
      formData.append('paymentEvidence', fileUploaded);
    }
    return formData;
  };

  const setFirstPage = () => {
    setValidatePaymentTable((prevState) => {
      return {
        ...prevState,
        page: 1,
        offset: 0,
      };
    });
  };

  const handleSinceDateChange = (e) => {
    if (e > dateTo && dateTo) {
      notify('Ingrese una fecha en un rango valido ', 'info');
    } else {
      setFirstPage();
      setDateFrom(e);
    }
  };

  const handleUntilDateChange = (e) => {
    if (e < dateFrom && dateFrom) {
      notify('Ingrese una fecha en un rango valido ', 'info');
    } else {
      setDateTo(e);
      setFirstPage();
    }
  };

  const selectitemsPerpage = (event) => {
    setValidatePaymentTable((prevState) => {
      return {
        ...prevState,
        page: 1,
        offset: 0,
        limit: parseInt(event.target.value),
      };
    });
  };

  const handlePageChange = (event, value) => {
    setValidatePaymentTable((prevState) => {
      return {
        ...prevState,
        page: value,
        offset: (value - 1) * validatePaymentTable?.limit,
      };
    });
  };

  const getDollarRate = async () => {
    rateService
      .getDollarRate()
      .then((response) => {
        if (response.status === 200) {
          setDollarRate(response?.data?.rate);
        } else {
          notify(`${response.message}` || 'error', 'error');
        }
      })
      .catch(() => {});
  };

  const getPaymentMethods = () => {
    productService
      .getPaymentMethods('sales')
      .then((response) => {
        if (response.status === 200) {
          setPaymentMethods(response?.data);
        } else {
          notify(`${response.message}`, 'error');
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    getUncheckedPayments();
  }, [
    validatePaymentTable?.page,
    validatePaymentTable?.offset,
    validatePaymentTable?.limit,
    validatePaymentTable?.type,
    dateFrom,
    dateTo,
  ]);

  const getUncheckedPayments = () => {
    let params = {
      from: dateFrom
        ? moment(dateFrom).format('MM-DD-YYYY')
        : moment().subtract(1, 'months').format('MM-DD-YYYY'),
      to: dateTo
        ? moment(dateTo).format('MM-DD-YYYY')
        : moment().format('MM-DD-YYYY'),
      limit: validatePaymentTable?.limit,
      page: validatePaymentTable?.offset,
    };

    paymentsService
      .listPaymentChanges(filterParams(params))
      .then((response) => {
        if (response.status === 200) {
          setValidatePaymentTable((prevState) => {
            return {
              ...prevState,
              data: response?.data?.data,
              count: response?.data?.total,
              isLoading: false,
            };
          });
        } else {
          notify(`${response.message}` || 'error', 'error');
          setValidatePaymentTable((prevState) => {
            return {
              ...prevState,
              isLoading: false,
            };
          });
        }
      })
      .catch((error) => {
        setValidatePaymentTable((prevState) => {
          return {
            ...prevState,
            isLoading: false,
          };
        });
      });
    return true;
  };

  const handleEditChangeModal = (payment) => {
    setOpenEditPaymentModal((prevState) => {
      return {
        ...prevState,
        isOpen: !openEditPaymentModal?.isOpen,
        payment: payment ? payment : '',
      };
    });

    if (payment) {
      setValue('paid', payment?.paidAmount);
      setValue('amount', payment?.changeAmount);
      setValue('amountBs', payment?.changeAmountBs);
      setValue('method', payment?.method);
      setValue('ref', payment?.reference);
      if (payment?.bank) {
        setValue('bank', payment?.bank);
      }
      if (payment?.phone) {
        setValue('phone', payment?.phone);
      }
      if (payment?.identificationType) {
        setValue('identificationType', payment?.identificationType);
      }
      if (payment?.identification) {
        setValue('identification', payment?.identification);
      }
      if (payment?.paymentDate) {
        setValue('date', new Date(payment?.paymentDate));
      }
    } else {
      reset();
    }
  };

  const editPayment = handleSubmit(async (data) => {
    let fileToPost = appendFile(data);

    if (!isLoading) {
      setIsLoading(true);
      paymentsService
        .editCash(fileToPost, openEditPaymentModal?.payment?._id)
        .then((response) => {
          if ([200, 201].includes(response.status)) {
            notify(`Pago editado con exito`, 'success');
            getUncheckedPayments();
            handleEditChangeModal();
            setIsLoading(false);
          } else {
            notify(`${response.message}`, 'error');
            setIsLoading(false);
          }
        })
        .catch((error) => {
          setIsLoading(false);
        });
    }
  });

  useEffect(() => {
    getPaymentMethods();
    getDollarRate();
  }, []);

    const shouldDisableReferenceAndReceipt = (paymentMethod) => {
      const role = authService?.getUserType();
      const hunter = salesRoles.SALES_HUNTER;
      const farmer = salesRoles.SALES_FARMER;
      const counter = salesRoles.SALES_COUNTER;
  
      const notAllowedRoles = [hunter, farmer, counter];
  
      // Nombres de métodos de pago a bloquear
      const blockedMethodNames = [
        'PAGO MÓVIL BANCAMIGA',
        'PAGO MÓVIL BNC',
        'PAGO MÓVIL MERCANTIL',
        'PAGO MÓVIL BANESCO',
        'PAGO MOVIL BANPLUS',
        'PAGO MÓVIL GENERICO',
        'PAGO MÓVIL',
        'C2P BANCAMIGA',
        'TRANSFERENCIA BANCAMIGA',
        'TRANSFERENCIA BNC',
        'TRANSFERENCIA MERCANTIL',
        'TRANSFERENCIA BANESCO',
        'TRANSFERENCIA BANPLUS',
        'TRANSFERENCIA GENERICO',
        'BANK TRANSFER',
        'ZELLE',
        'PAYPAL',
        'BINANCE',
      ];
  
      const selectedMethod = paymentMethods.find((p) => p._id === paymentMethod);
      const isBlocked = selectedMethod
        ? blockedMethodNames.includes(selectedMethod.name)
        : blockedMethodNames.includes(paymentMethod);
  
      // Si el rol es hunter, farmer o counter (interno o externo)
      // y el método de pago es uno de los bloqueados
      if (notAllowedRoles.includes(role?.code || role) && isBlocked) {
        return true;
      }
      return false;
    }; 

  return (
    <>
      <Header />
      <LoadSpinner show={isLoading} />
      <EditChangeModal
        modal={openEditPaymentModal}
        register={register}
        action={editPayment}
        handleClose={() => handleEditChangeModal()}
        errors={errors}
        setValue={setValue}
        control={control}
        options={{
          paymentMethods: paymentMethods,
          paymentTypesOptions: paymentTypesOptions,
          dollarRate: dollarRate,
        }}
        watch={watch}
        fileUploaded={fileUploaded}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        setFileUploaded={setFileUploaded}
        shouldDisableReferenceAndReceipt={shouldDisableReferenceAndReceipt}
      />
      <div className="service-with-container container">
        <div className="buttons-container">
          <h1 className="title-component">{`Vueltos: ${validatePaymentTable.count}`}</h1>
        </div>
        <div className="breadcrumb-container">
          <div className="exportables-buttons-container">
            <div style={{ width: '100%' }} className="row">
              <FilterDatePicker
                bootstrap={'col-12 col-md-2'}
                label={'Desde'}
                type={'date'}
                name={'dateFrom'}
                placeholder={''}
                onChange={handleSinceDateChange}
                value={dateFrom}
                disabled={validatePaymentTable?.isLoading}
              />
              <FilterDatePicker
                bootstrap={'col-12 col-md-2'}
                label={'Hasta'}
                type={'date'}
                name={'dateTo'}
                placeholder={''}
                onChange={handleUntilDateChange}
                value={dateTo}
                disabled={validatePaymentTable?.isLoading}
              />

              <div
                style={{
                  marginTop: '24px',
                  marginLeft: 'auto',
                  width: '350px',
                }}
              >
                {![
                  salesRoles?.SALES_FARMER,
                  salesRoles?.SALES_HUNTER,
                  salesRoles?.SALES_COUNTER,
                  salesRoles?.SALES_ADMIN_AUX,
                  salesRoles?.SALES_KEY_ACCOUNTS,
                ].includes(authService?.getUserType()) && (
                  <SelectPerPage
                    label={'Registros por página'}
                    options={itemsPerpage}
                    value={validatePaymentTable?.limit}
                    onChange={selectitemsPerpage}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <PaymentsChangesTable
          header={paymentChangesTableHeader}
          data={validatePaymentTable}
          handlePageChange={handlePageChange}
          tableRef={tableRef}
          dateFrom={dateFrom}
          dateTo={dateTo}
          action={handleEditChangeModal}
        />
      </div>
    </>
  );
};
